import React, { useState, useMemo } from 'react';
import {
  Users,
  Filter,
  AlertCircle,
} from 'lucide-react';
import { HelpTrigger } from '../../help';
import ReportLogoHeader from './ReportLogoHeader';
import VotesMatrixTable from './VotesMatrixTable';
import { applyVotesReportClientFilters } from './votesMatrixUtils';
import { PARTY_MAJOR_FILTER_OPTIONS } from '../../constants/parties';
import { matchesPartyFilter } from '../../utils/partyUtils';
import LoadingList from '@shared/ui/LoadingList';
import ExportMenu from '../ExportMenu';
import EmptyState from '../EmptyState';

interface Note {
  id: string;
  title: string;
  content: string;
  author: string;
  created_at: string;
  updated_at: string;
  tags: string[];
}

interface LegislatorVote {
  billId: number;
  billNumber: string;
  billTitle: string;
  vote: string;
  voteDate: string;
  voteChamber: string;
  voteContext: string;
  voteType: string;
  action: string;
  motion: string;
  isAligned: boolean | null;
  billNotes: Note[];
}

interface LegislatorBreakdown {
  legislatorId: number;
  legislatorName: string;
  party: string;
  chamber: string;
  trackingPosition: string;
  isPriority: boolean;
  totalVotesOnTrackedBills: number;
  thirdReadingVotes: number;
  alignedVotes: number;
  alignmentPercentage: number;
  relatedNotes: Note[];
  votes: LegislatorVote[];
  workspaces?: { id?: string; name: string; position?: string }[];
}

interface BillVotingLegislator {
  legislatorId: number;
  legislatorName: string;
  party: string;
  vote: string;
  isAligned: boolean | null;
  legislatorNotes: Note[];
}

interface BillBreakdown {
  billId: number;
  billNumber: string;
  billTitle: string;
  trackingPosition: string;
  isHot: boolean;
  relatedNotes: Note[];
  votingLegislators: BillVotingLegislator[];
}

interface ReportSummary {
  totalTrackedLegislators: number;
  totalTrackedBills: number;
  totalVoteMatches: number;
  totalRelevantNotes: number;
  houseAlignment: number;
  senateAlignment: number;
}

interface GroupedBillNotes {
  billId: number;
  billNumber: string;
  billTitle: string;
  notes: Note[];
}

interface GroupedLegislatorNotes {
  legislatorId: number;
  legislatorName: string;
  party: string;
  chamber: string;
  notes: Note[];
}

interface ReportData {
  summary: ReportSummary;
  legislatorBreakdown: LegislatorBreakdown[];
  billBreakdown: BillBreakdown[];
  reportNotes: {
    billNotes: Note[];
    legislatorNotes: Note[];
    generalNotes: Note[];
    allBillNotesGrouped?: GroupedBillNotes[];
    allLegislatorNotesGrouped?: GroupedLegislatorNotes[];
  };
  metadata: {
    generatedAt: string;
    filters: Record<string, unknown>;
    queryParameters: Record<string, unknown>;
  };
}

interface LegislatorVotesOnTrackedBillsReportProps {
  workspaceId: string;
  data: ReportData | null;
  workspaceName?: string;
  loading: boolean;
  error: string | null;
  onFiltersChange: (filters: Record<string, unknown>) => void;
  onExport: (format: 'excel' | 'csv' | 'pdf') => void;
  isOrgMode?: boolean;
}

const LegislatorVotesOnTrackedBillsReport: React.FC<LegislatorVotesOnTrackedBillsReportProps> = ({
  workspaceId,
  data,
  workspaceName = 'Workspace',
  loading,
  error,
  onFiltersChange,
  onExport,
  isOrgMode = false,
}) => {
  // Hide bills/legislators with no votes — reduces matrix size for sparse org-level data.
  const [hideNoVotes, setHideNoVotes] = useState(true);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    legislatorParty: '',
    legislatorChamber: '',
    billPosition: '',
    legislatorPosition: '',
    priorityOnly: false,
    hotBillsOnly: false,
    voteType: '',
    voteContext: '',
    hasNotes: false,
    noteAuthor: '',
    noteTags: '',
    noteSearch: ''
  });

  const handleFilterChange = (key: string, value: unknown) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const displayData = useMemo(() => {
    if (!data) return null;
    if (!isOrgMode) return data;
    return applyVotesReportClientFilters(data, filters, matchesPartyFilter);
  }, [data, filters, isOrgMode]);

  const handleExport = (format: 'excel' | 'csv' | 'pdf') => {
    onExport(format);
  };

  const getAlignmentColor = (pct: number) => {
    if (pct >= 80) return 'text-success-600 dark:text-success-400';
    if (pct >= 60) return 'text-warning-600 dark:text-warning-400';
    return 'text-error-600 dark:text-error-400';
  };

  if (loading) {
    return (
      <LoadingList rows={5} label="Loading report" />
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-error-500 mr-2" />
          <span className="text-error-700 dark:text-error-300">{error}</span>
        </div>
      </div>
    );
  }

  if (!displayData) {
    return (
      <EmptyState hint="No data available" />
    );
  }

  if (displayData && displayData.summary?.totalVoteMatches === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
                {workspaceName} - Votes Report
              </h2>
              <span className="hidden md:inline-flex">
                <HelpTrigger contentKey="workspace-votes" size="md" />
              </span>
            </div>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
              How legislators tracked in this workspace voted on bills tracked in this workspace
            </p>
          </div>
        </div>
        <div className="card p-12 text-center">
          <div className="max-w-md mx-auto">
            <Users className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
              No Vote Data Found
            </h3>
            <p className="text-text-secondary dark:text-text-dark-secondary mb-4">
              No votes found by tracked legislators on tracked bills in this workspace.
            </p>
            <ul className="text-sm text-text-tertiary dark:text-text-dark-tertiary list-disc list-inside space-y-1 mt-2 text-left">
              <li>No legislators are tracked in this workspace yet</li>
              <li>No bills are tracked in this workspace yet</li>
              <li>Tracked legislators haven&apos;t voted on tracked bills yet</li>
              <li>Vote data hasn&apos;t been imported yet</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ReportLogoHeader workspaceId={workspaceId} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold text-left text-text-primary dark:text-text-dark-primary">
              {workspaceName} - Votes Report
            </h2>
            <span className="hidden md:inline-flex">
              <HelpTrigger contentKey="workspace-votes" size="md" />
            </span>
          </div>
          <p className="text-sm text-left text-text-secondary dark:text-text-dark-secondary mt-1">
            Tracked legislator votes on tracked bills
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-2 text-sm border border-border-light dark:border-border-dark rounded-md bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary dark:text-text-dark-secondary hover:bg-neutral-200 dark:hover:bg-background-dark-quaternary flex items-center"
          >
            <Filter className="w-4 h-4 mr-1" />
            Filters
          </button>
          <ExportMenu
            formats={['csv', 'excel', 'pdf']}
            /* This report has no card/word mode, so the id set narrows. */
            onExport={(format) => handleExport(format as 'csv' | 'excel' | 'pdf')}
          />
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                Legislator Party
              </label>
              <select
                value={filters.legislatorParty}
                onChange={(e) => handleFilterChange('legislatorParty', e.target.value)}
                className="w-full text-sm px-2 py-1 border border-border-light dark:border-border-dark rounded bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary"
              >
                <option value="">All Parties</option>
                {PARTY_MAJOR_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                Chamber
              </label>
              <select
                value={filters.legislatorChamber}
                onChange={(e) => handleFilterChange('legislatorChamber', e.target.value)}
                className="w-full text-sm px-2 py-1 border border-border-light dark:border-border-dark rounded bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary"
              >
                <option value="">All Chambers</option>
                <option value="House">House</option>
                <option value="Senate">Senate</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                Vote Type
              </label>
              <select
                value={filters.voteType}
                onChange={(e) => handleFilterChange('voteType', e.target.value)}
                className="w-full text-sm px-2 py-1 border border-border-light dark:border-border-dark rounded bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary"
              >
                <option value="">All Votes</option>
                <option value="Y">Yes</option>
                <option value="N">No</option>
                <option value="E">Excused</option>
                <option value="V">Absent</option>
              </select>
            </div>
            <div className="flex items-center">
              <label className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
                <input
                  type="checkbox"
                  checked={filters.priorityOnly}
                  onChange={(e) => handleFilterChange('priorityOnly', e.target.checked)}
                  className="mr-2"
                />
                Priority Only
              </label>
            </div>
            <div className="flex items-center">
              <label className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
                <input
                  type="checkbox"
                  checked={filters.hasNotes}
                  onChange={(e) => handleFilterChange('hasNotes', e.target.checked)}
                  className="mr-2"
                />
                Has Notes
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Compact Summary Bar */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-text-secondary dark:text-text-dark-secondary">
        <span>
          <strong className="text-primary-600">{displayData.summary.totalTrackedLegislators || 0}</strong> Tracked Legislators
        </span>
        <span>
          <strong className="text-success-600">{displayData.summary.totalTrackedBills || 0}</strong> Tracked Bills
        </span>
        <span>
          <strong className="text-info-600">{displayData.summary.totalVoteMatches || 0}</strong> Vote Matches
        </span>
        <span>
          <strong className={getAlignmentColor(displayData.summary.houseAlignment || 0)}>
            {displayData.summary.houseAlignment || 0}%
          </strong>{' '}
          House Alignment
        </span>
        <span>
          <strong className={getAlignmentColor(displayData.summary.senateAlignment || 0)}>
            {displayData.summary.senateAlignment || 0}%
          </strong>{' '}
          Senate Alignment
        </span>
      </div>

      {/* Vote Matrix */}
      <VotesMatrixTable
        billBreakdown={displayData.billBreakdown || []}
        legislatorBreakdown={displayData.legislatorBreakdown || []}
        isOrgMode={isOrgMode}
        hideNoVotes={hideNoVotes}
        onHideNoVotesChange={setHideNoVotes}
        workspaceId={workspaceId}
      />
    </div>
  );
};

export default LegislatorVotesOnTrackedBillsReport;
